import * as React from 'react';
import styled from 'styled-components';

import {
  AffirmProvider,
  useConfig,
  useContractContext,
  useFeatures,
  useStrings,
} from 'src/providers';

import AddAndDeclineProtection from 'src/components/Shared/AddAndDeclineProtection';
import AffirmInfo from 'src/components/Modal/Main/AffirmInfo';
import ContractSelector from 'src/components/ContractSelector';
import ContractDetails from 'src/components/ContractDetails';
import TermsLink from './TermsLink';
import { Disclaimer } from 'src/components/Disclaimer';

export interface IMainProps {
  onFAQClicked: React.MouseEventHandler<HTMLButtonElement>;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
}

const StyledMain = styled.div`
  --spacing: 24px;

  @media (min-width: 551px) {
    --spacing: 32px;
  }

  position: relative;
  margin-top: var(--spacing);
  display: flex;
  flex-direction: column;
  gap: var(--spacing);

  .main-tab__product-name {
    width: 100%;
    background-color: var(--background-gray);
    font-weight: 500;
    text-align: center;
    line-height: 1.2;
    border-radius: 6px;
    margin: 15px auto;
    padding: 15px 25px 13px;

    p {
      padding: 0;
      margin: 0;
      overflow: hidden;
      visibility: visible;
      text-overflow: ellipsis;
      word-wrap: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      max-height: 66px;
    }
  }

  @media (max-width: 550px ) and (min-height: 660px) {
    position: static;
    min-height: auto;
    padding-bottom: 0;
  }

  h1 {
    font-size: 24px;
    line-height: 1.3;
    margin: 0;

    @media (min-width: 551px) {
      font-size: 36px;
      line-height: 1.2;
    }
  }
`;

const Main = ({ onFAQClicked, closeButtonRef }: IMainProps) => {
  const { productName, ipidUrl } = useContractContext();
  const { prioritizeTermsPosition, showIpidDisclaimer } = useFeatures();
  const { headline } = useStrings();
  const { showAgeRestrictionDisclaimer, phrases } = useConfig();

  React.useEffect(() => {
    if (closeButtonRef?.current) {
      closeButtonRef?.current?.focus();
    }
  }, [closeButtonRef]);

  const maybeRenderProductName = () => {
    if (productName === '') {
      return null;
    }

    return <div className='main-tab__product-name'><p>{productName}</p></div>;
  };

  const buildAddandDeclineSection = () => {
    if (showAgeRestrictionDisclaimer || showIpidDisclaimer) {
      return <>
        <AddAndDeclineProtection />
        <Disclaimer
          disclaimer={showAgeRestrictionDisclaimer ? phrases['modal.ageRestrictionDisclaimer'] : null}
          ipidUrl={showIpidDisclaimer ? ipidUrl : null}
        />
      </>;
    }
    return <>
      {prioritizeTermsPosition && <TermsLink position='over' />}
      <AddAndDeclineProtection />
      {!prioritizeTermsPosition && <TermsLink position='under' />}
    </>;
  };

  return (
    <StyledMain className='main-tab' id='plandata_tab'>
      <h1 className='main-tab__header' data-test='modal-header-text'>
        {headline}
      </h1>

      {maybeRenderProductName()}

      <ContractSelector />
      <AffirmProvider>
        <AffirmInfo />
      </AffirmProvider>

      <ContractDetails onChangeTab={onFAQClicked} />

      { /* MARKER AJC: After all components are finished, control placement of below with flex order property? */}
      {buildAddandDeclineSection()}

    </StyledMain>
  );
};

export default Main;

